'use client';

/**
 * mixpanel-browser is ~300 KB that no screen needs before it can render, so it
 * is loaded on demand when an app calls initMixpanel. The public surface stays
 * synchronous: init marks the client ready and queues every call made while
 * the SDK downloads, then flushes them in order — callers keep the same
 * "init, then fire events" contract they had when the import was static.
 */

type MixpanelClient = typeof import('mixpanel-browser');

interface IMixpanelInitConfig {
  token: string;
  debug?: boolean;
  autocapture?: boolean;
  /** When false, events are sent immediately instead of Mixpanel's default ~5s batch flush. */
  batchRequests?: boolean;
}

let initialized = false;
let client: MixpanelClient | undefined;
let pendingCalls: Array<(mixpanel: MixpanelClient) => void> = [];

const withMixpanel = (call: (mixpanel: MixpanelClient) => void): void => {
  if (client) {
    call(client);
    return;
  }
  pendingCalls.push(call);
};

export const initMixpanel = (config: IMixpanelInitConfig): void => {
  if (typeof window === 'undefined') return;
  initialized = true;
  void import('mixpanel-browser')
    .then(({ default: mixpanel }) => {
      mixpanel.init(config.token, {
        api_host: 'https://api-eu.mixpanel.com',
        debug: config.debug ?? false,
        autocapture: config.autocapture ?? false,
        ...(config.batchRequests !== undefined
          ? { batch_requests: config.batchRequests }
          : {}),
      });
      client = mixpanel as unknown as MixpanelClient;
      pendingCalls.splice(0).forEach((call) => call(client as MixpanelClient));
    })
    .catch(() => {
      // Analytics failing to download must never surface; the queued events
      // are dropped exactly as they were when the SDK could not be reached.
      pendingCalls = [];
    });
};

export const trackMixpanelEvent = (
  name: string,
  properties?: Record<string, unknown>
): void => {
  if (!initialized) return;
  withMixpanel((mixpanel) => mixpanel.track(name, properties));
};

export const identifyMixpanelUser = (distinctId: string): void => {
  if (!initialized) return;
  withMixpanel((mixpanel) => mixpanel.identify(distinctId));
};

export const resetMixpanelSession = (): void => {
  if (!initialized) return;
  withMixpanel((mixpanel) => mixpanel.reset());
};

export const mixpanelOptIn = (): void => {
  if (!initialized) return;
  withMixpanel((mixpanel) => mixpanel.opt_in_tracking());
};

export const mixpanelOptOut = (): void => {
  if (!initialized) return;
  withMixpanel((mixpanel) => mixpanel.opt_out_tracking());
};

export const isMixpanelReady = (): boolean => initialized;
